// Copyright (c) 2022 by Move Closer

import { Injectable, IResponse, mapCollection, ResourceActionFailed } from '@movecloser/front-core'

import { CartData, CartIdentifier } from '../../../../contexts/checkout/contracts'

import { ConnectorErrors, resolveFromStatus } from '../../../../support'
import { CartModel } from '../../contracts/models'

import { updatedItemsAdapterMap } from '../../models/cart.adapter'

import { CartAwareAbstract } from '../cart-aware.abstract'
import { ICartService, UpdatedItem } from './contracts'

/**
 * @author Javlon Khalimjonov <javlon.khalimjonov@movecloser.pl> (original)
 * @author Filip Rurak <filip.rurak@movecloser.pl>
 */
@Injectable()
export class CartService extends CartAwareAbstract implements ICartService {
  /**
   * @inheritDoc
   */
  public async addToCart (cartId: CartIdentifier, sku: string, quantity: number = 1): Promise<CartModel> {
    // Single cart item
    const candidate = {
      quantity,
      sku
    }

    const response: IResponse = await this.connector.call('addCartItems', { cartId, items: [candidate] })

    if (!response.isSuccessful() && response.errors && !response.data.addProductsToCart) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    if (response.data.addProductsToCart.user_errors?.length) {
      throw new ResourceActionFailed(
        response.data.addProductsToCart.user_errors[0].message,
        ConnectorErrors.Validation,
        response.data
      )
    }

    return await this.composeCart(response.data.addProductsToCart.cart)
  }

  /**
   * @inheritDoc
   */
  public async applyCoupon (cartId: CartIdentifier, code: string): Promise<CartModel> {
    const response: IResponse = await this.connector.call('applyCoupon', { cartId, code })

    if (!response.isSuccessful() && response.errors && !response.data.applyCouponToCart) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.applyCouponToCart.cart)
  }

  public async clearCoupons (cartId: CartIdentifier): Promise<CartModel> {
    const response: IResponse = await this.connector.call('clearCoupons', { cartId })

    if (!response.isSuccessful() && response.errors && !response.data.removeCouponFromCart) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.removeCouponFromCart.cart)
  }

  /**
   * @inheritDoc
   */
  public async loadCustomerCart (): Promise<CartModel> {
    const response: IResponse = await this.connector.call('loadCustomerCart', {})

    if (!response.isSuccessful() && response.errors && !response.data.customerCart) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.customerCart)
  }

  /**
   * @inheritDoc
   */
  public async mergerCarts (source: CartIdentifier, destination: CartIdentifier): Promise<CartModel> {
    const response: IResponse = await this.connector.call('mergeCarts', { source, destination })

    if (!response.isSuccessful() && response.errors && !response.data.mergeCarts) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.mergeCarts)
  }

  /**
   * @inheritDoc
   */
  public async removeFromCart (cartId: CartIdentifier, uid: string): Promise<CartModel> {
    const response: IResponse = await this.connector.call('removeFromCart', { cartId, uid })

    if (!response.isSuccessful() && response.errors && !response.data.removeItemFromCart) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.removeItemFromCart.cart)
  }

  /**
   * @inheritDoc
   */
  public async resolveCart (id: string, tries?: number): Promise<CartModel> {
    const response = await this.loadCartById(id)

    if (tries === 0) {
      throw new Error('[CartService]: Exceeded number of requests to resolve cart!')
    }

    if (!response.isSuccessful() && response.errors && (typeof tries !== 'undefined' && tries !== 0)) {
      const newCartId: CartIdentifier = await this.createEmptyCart()
      return this.resolveCart(newCartId, --tries)
    }

    return await this.composeCart(response.data.cart)
  }

  /**
   * @inheritDoc
   */
  public async resolveEmptyCart (): Promise<CartModel> {
    try {
      const newCartId: CartIdentifier = await this.createEmptyCart()

      const candidate = { id: newCartId, items: [] } as unknown as CartData

      return await this.composeCart(candidate)
    } catch (e: any) {
      throw new Error(e)
    }
  }

  /**
   * @inheritDoc
   */
  public async updateCartItem (cartId: CartIdentifier, uid: string, quantity: number): Promise<CartModel> {
    const response: IResponse = await this.connector.call('updateCartItem', { cartId, uid, quantity })

    if (!response.isSuccessful() && response.errors && !response.data.updateCartItems) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.updateCartItems.cart)
  }

  /**
   * @inheritDoc
   */
  public async updateCartItems (cartId: CartIdentifier, updatedItems: Array<UpdatedItem>): Promise<CartModel> {
    const mapped = mapCollection(updatedItems, updatedItemsAdapterMap, false)
    const response: IResponse = await this.connector.call('updateCartItems', { cartId, updatedItems: mapped })

    if (!response.isSuccessful() && response.errors && !response.data.updateCartItems) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return await this.composeCart(response.data.updateCartItems.cart)
  }

  /**
   * Creates empty cart.
   */
  protected async createEmptyCart (): Promise<CartIdentifier> {
    const response: IResponse = await this.connector.call('createEmptyCart', {})

    if (!response.isSuccessful() && response.errors) {
      throw new ResourceActionFailed(
        response.errors[0].message,
        resolveFromStatus(response.status),
        response.data
      )
    }

    return response.data.createEmptyCart
  }
}
